import { Text, useBoolean } from '@chakra-ui/react';
import { ReactNode, memo, useCallback, useEffect, useState } from 'react';

type TypingTextProps = {
  children?: (text: string, testId?: string) => ReactNode;
  text: string;
  typingSpeed?: number; // Speed in milliseconds, default is 100ms
  delayPerLine?: number;
  forceStop?: boolean;
  iterationCount?: number;
  onComplete?: () => void;
};

/**
 * Render the text typing animation
 * @param text The text to be typed out
 * @param typingSpeed The speed of typing in milliseconds, default is 100ms
 * @param delayPerLine The delay between each line in milliseconds, default is 0ms
 * @param forceStop Force stop the typing animation
 * @param iterationCount The number of times the animation should repeat
 * default is 1
 * infinite is -1
 * no animation show all text is 0
 * @param onComplete Callback when the typing animation is completed
 */
export const TypingText = memo(
  ({
    children,
    text,
    typingSpeed = 100,
    delayPerLine = 0,
    forceStop = false,
    iterationCount = 1,
    onComplete = () => {},
  }: TypingTextProps) => {
    const [displayedText, setDisplayedText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useBoolean(false);
    const [activeIterationCount, setActiveIterationCount] = useState(1);

    // Reset the typing component state
    const reset = useCallback(() => {
      setDisplayedText('');
      setCurrentIndex(0);
      setIsPaused.off();
    }, [setIsPaused]);

    const handleComplte = useCallback(() => {
      // Infinite
      if (iterationCount === -1) {
        setTimeout(() => {
          reset();
        }, typingSpeed);
        return;
      }

      // Trigger the next iteration
      if (activeIterationCount < iterationCount) {
        setTimeout(() => {
          setActiveIterationCount((prev) => prev + 1);
          reset();
        }, typingSpeed);
        return;
      }

      onComplete();
    }, [typingSpeed, iterationCount, activeIterationCount, reset, setActiveIterationCount, onComplete]);

    useEffect(() => {
      if (iterationCount === 0) {
        setDisplayedText(text);
        setCurrentIndex(text.length);
        handleComplte();
        return;
      }

      if (isPaused) return;

      // Only start the effect if the text isn't fully typed out yet
      if (currentIndex < text.length) {
        const isNewLine = text[currentIndex] === '\n';
        const isFirstCharacter = currentIndex === 0;
        const delay = isNewLine
          ? delayPerLine + typingSpeed // Add delay for new line
          : isFirstCharacter && activeIterationCount === 1
          ? 0 // No delay for the first line
          : typingSpeed;
        const timeout = setTimeout(() => {
          setDisplayedText((prevText) => prevText + text[currentIndex]);
          setCurrentIndex((prevIndex) => prevIndex + 1);
        }, delay);

        // Cleanup the timeout on effect cleanup
        return () => {
          clearTimeout(timeout);
        };
      } else {
        handleComplte();
      }
    }, [
      delayPerLine,
      iterationCount,
      text,
      typingSpeed,
      isPaused,
      displayedText,
      currentIndex,
      activeIterationCount,
      handleComplte,
    ]);

    // Force stop the typing animation when it changed to true
    useEffect(() => {
      if (forceStop) setIsPaused.on();
    }, [forceStop, setIsPaused]);

    // Reset
    useEffect(() => reset(), [text, reset]);

    const TypingTextTestId = 'typing-text';

    if (children) {
      return <>{children(displayedText, TypingTextTestId)}</>;
    }

    return (
      <Text whiteSpace='pre-line' data-testid={TypingTextTestId}>
        {displayedText}
      </Text>
    );
  },
);

TypingText.displayName = 'TypingText';
